import { createSlice } from '@reduxjs/toolkit';
import { toLocalDateString } from '../../utils/dates';
import {
  fetchPickupCouriers,
  fetchPickupWarehouses,
  fetchPickupRequests,
  fetchEligibleOrders,
  createPickupRequest,
  registerManualPickup,
  assignOrdersToPickup,
  requestManifest,
} from './pickupRequestsThunks';

/**
 * What the legacy `usePickupRequests` hook kept in `useState`.
 *
 * Built by a function so each reset starts the range on today — the hook's way
 * of keeping the first load from pulling the whole history — rather than on the
 * day the module was loaded. One `loading` and one `error` for every action, as
 * in the hook, except the booking: its failure belongs to the form, in
 * `createError`. What to say once an action goes through is a toast, see
 * `pickupRequestsFeedback`.
 */
const buildInitialState = () => ({
  pickupRequests: [],
  eligibleOrders: [],
  availableCouriers: [],
  /** Set once the couriers answered either way, so "none enabled" is not a guess. */
  couriersLoaded: false,
  warehouses: [],
  loadingWarehouses: false,
  /** Shown in the booking dialog, which covers the page's banner. */
  warehousesError: null,
  /**
   * Why the last booking failed, `{message, fieldErrors, saved}`; the form shows
   * it. `saved` when the backend kept the request as failed.
   */
  createError: null,
  filters: { courier: '', date_from: toLocalDateString(new Date()), date_to: '' },
  loading: false,
  error: null,
});

const startLoading = (state) => {
  state.loading = true;
  state.error = null;
};

const failWithError = (state, action) => {
  state.loading = false;
  state.error = action.payload;
};

const pickupRequestsSlice = createSlice({
  name: 'pickupRequests',
  initialState: buildInitialState(),
  reducers: {
    setPickupFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearPickupCreateError: (state) => {
      state.createError = null;
    },
    clearPickupError: (state) => {
      state.error = null;
    },
    /**
     * Each screen resets on leaving, so the next one starts the way a fresh
     * hook did in the legacy: no stale error, filters back on today.
     */
    resetPickupRequests: () => buildInitialState(),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPickupCouriers.fulfilled, (state, action) => {
        state.availableCouriers = action.payload || [];
        state.couriersLoaded = true;
      })
      // Without this the rejection was swallowed, and an empty list after a
      // failed fetch read as "you have no couriers". It does not clear
      // `loading`: the screen fires this alongside `fetchPickupRequests`, and
      // that one owns the spinner.
      .addCase(fetchPickupCouriers.rejected, (state, action) => {
        state.error = action.payload;
        state.couriersLoaded = true;
      })

      .addCase(fetchPickupRequests.pending, startLoading)
      .addCase(fetchPickupRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.pickupRequests = action.payload || [];
      })
      .addCase(fetchPickupRequests.rejected, failWithError)

      .addCase(fetchEligibleOrders.pending, startLoading)
      .addCase(fetchEligibleOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.eligibleOrders = action.payload || [];
      })
      .addCase(fetchEligibleOrders.rejected, failWithError)

      .addCase(fetchPickupWarehouses.pending, (state) => {
        state.loadingWarehouses = true;
        state.warehousesError = null;
      })
      .addCase(fetchPickupWarehouses.fulfilled, (state, action) => {
        state.loadingWarehouses = false;
        state.warehouses = action.payload || [];
      })
      .addCase(fetchPickupWarehouses.rejected, (state, action) => {
        state.loadingWarehouses = false;
        state.warehouses = [];
        state.warehousesError = action.payload;
      })

      .addCase(createPickupRequest.pending, (state) => {
        state.createError = null;
      })
      .addCase(createPickupRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.pickupRequests = [action.payload, ...state.pickupRequests];
      })
      .addCase(createPickupRequest.rejected, (state, action) => {
        const { message, fieldErrors, pickup } = action.payload || {};
        state.createError = { message, fieldErrors: fieldErrors || {}, saved: Boolean(pickup) };
        if (pickup) state.pickupRequests = [pickup, ...state.pickupRequests];
      })

      .addCase(registerManualPickup.pending, (state) => {
        state.createError = null;
      })
      .addCase(registerManualPickup.fulfilled, (state, action) => {
        state.pickupRequests = [action.payload, ...state.pickupRequests];
      })
      .addCase(registerManualPickup.rejected, (state, action) => {
        const { message, fieldErrors } = action.payload || {};
        state.createError = { message, fieldErrors: fieldErrors || {}, saved: false };
      })

      .addCase(assignOrdersToPickup.pending, startLoading)
      .addCase(assignOrdersToPickup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignOrdersToPickup.rejected, failWithError)

      .addCase(requestManifest.pending, startLoading)
      .addCase(requestManifest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestManifest.rejected, failWithError);
  },
});

export const {
  setPickupFilters,
  clearPickupError,
  clearPickupCreateError,
  resetPickupRequests,
} = pickupRequestsSlice.actions;

export default pickupRequestsSlice.reducer;
